import { Sort } from '@angular/material/sort';
import { MultiplePaginationParams, MultipleSortParams, PaginatorParams, SortParams } from '@nagel-types';
import { FormGroup } from '@angular/forms';

function getSortDirection(direction: string) {
    let sortDirection = 0;
    if (direction.toLowerCase() === 'desc') {
        sortDirection = 1;
    }

    return sortDirection;
}

export function getCalcFieldSortObject(fieldName: string) {
    return {
        fieldName,
        sortDirection: getSortDirection('desc'),
        isCalc: true
    };
}

export function generatePaginationParams(
    pageIndex: number,
    pageSize: number,
    sort: Sort
): PaginatorParams {
    const sortParams: SortParams = sort.active && sort.direction
        ? {
            fieldName: sort.active,
            sortDirection: getSortDirection(sort.direction)
        }
        : {
            fieldName: '',
            sortDirection: 0
        };
    return {
        offsetInfo: {
            offset: pageIndex * pageSize,
            limit: pageSize
        },
        sortColumn: sortParams
    };
}

export function generateMultipleColumnPaginationParams(
    offset: number,
    pageSize: number,
    sortParams: MultipleSortParams[]
): MultiplePaginationParams {
    return {
        offsetInfo: {
            offset,
            limit: pageSize
        },
        sortOrder: sortParams
    };
}

export function capitalizeFirstLetter(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
}

export function getAreObjectsMatching<T extends {[key: string]: unknown}>(object1: T, object2: T): boolean {
    return Object.entries(object1)?.every(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
            return value && object2[key] && getAreObjectsMatching(value as T, object2[key] as T);
        }

        //eslint-disable-next-line
        return object1[key] == object2[key];
    });
}

export function changeFormFieldState (form: FormGroup, fieldName: string, shouldDisable: boolean, defaultValue?: unknown) {
    const selectedField = form.get(fieldName);

    if (shouldDisable) {
        selectedField?.disable({ emitEvent: false });

        if (defaultValue !== undefined) {
            selectedField?.setValue(defaultValue, { emitEvent: false });
        }
    } else if (selectedField?.disabled) {
        selectedField?.enable({ emitEvent: false });
    }
}

export const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

export const isNullOrUndefinedOrEmpty = (value: string | null | undefined): boolean => {
    let result = isNullOrUndefined(value);

    if (!result && typeof value === 'string') {
        result = value.trim() === '';
    }

    return result;
};

export const isEmptyObject = (obj: object = {}) => {
    return Object.keys(obj).length === 0;
};

export const getStringBeforeLastOccurence = (str: string, marker: string) => {
    const lastIndex: number = str.lastIndexOf(marker);

    return lastIndex !== -1 ? str.substring(0, lastIndex) : str;
};

export const getIcons = (icon: string | string[]) : string[] => {
    let result :string[] = [];
    Array.isArray(icon)
        ? result = icon
        : result.push(icon);
    return result;
};

export const lowercaseKeys = <T>(obj: T): any => {
    if (Array.isArray(obj)) {
        return obj.map(lowercaseKeys);
    } else if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
                key.charAt(0).toLowerCase() + key.slice(1),
                lowercaseKeys(value),
            ])
        );
    }

    return obj;
};

const DEFAULT_MAX_ID_NUMBER = 1000000;

export const generateRandomNumberId = (max: number = DEFAULT_MAX_ID_NUMBER): number => {
    return Math.floor(Math.random() * max);
};

const KILOMETERS_IN_METER = 1000;

export const convertMetersToKilometers = (meters: number) => {
    return meters / KILOMETERS_IN_METER;
};

const ROUNDING_NUMBER = 100;

export const roundToTwoDecimalPlaces = (num: number) => {
    return Math.round(num * ROUNDING_NUMBER) / ROUNDING_NUMBER;
};